import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const companyColors = [
  { bg: "bg-primary/15", text: "text-primary", glow: "glow-card" },
  { bg: "bg-green-500/15", text: "text-green-400", glow: "glow-card" },
  { bg: "bg-accent/15", text: "text-accent", glow: "glow-card glow-card-gold" },
  { bg: "bg-sky-500/15", text: "text-sky-400", glow: "glow-card" },
];

export const CompaniesSection = ({ companies }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="companies"
      className="py-16 md:py-24 scroll-mt-24 relative"
      data-testid="companies-section"
      aria-label="Companies and Businesses"
      ref={ref}
    >
      <div className="mx-auto max-w-[var(--page-max)] px-4 md:px-6">
        <motion.div
          variants={container}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
        >
          <motion.div variants={reveal} className="text-center mb-12">
            <span className="mono-label text-primary mb-3 block">Ventures</span>
            <h2 className="text-xl sm:text-2xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
              Companies & Businesses
            </h2>
            <p className="text-sm text-muted-foreground mt-3 max-w-lg mx-auto">
              Building diverse businesses across gaming, agriculture, security, and food & beverage.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {companies.map((company, i) => {
              const colors = companyColors[i % companyColors.length];
              return (
                <motion.div key={company.name} variants={reveal}>
                  <Card
                    className={`p-6 bg-card border-border h-full flex flex-col ${colors.glow}`}
                    data-testid={`company-card-${company.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {/* Initials */}
                    <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center mb-4`}>
                      <span className={`text-lg font-bold ${colors.text}`} style={{ fontFamily: 'var(--font-display)' }}>
                        {company.initials}
                      </span>
                    </div>

                    <h3 className="font-semibold text-base mb-2" style={{ fontFamily: 'var(--font-display)' }}>
                      {company.name}
                    </h3>

                    <p className="text-sm text-muted-foreground leading-[1.55] flex-grow mb-4">
                      {company.description}
                    </p>

                    <Button
                      asChild
                      variant="secondary"
                      size="sm"
                      className="bg-secondary text-secondary-foreground border border-border hover:border-primary/50 w-full"
                    >
                      <a
                        href={company.url}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`Visit ${company.name}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-2" />
                        Visit Site
                      </a>
                    </Button>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
